import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { UsersService } from '../../../shared/services/users.service';
import { PatchUserRequest, User } from '../../../shared/types/user.types';
import {
  UserFormComponent,
  UserFormPayload,
} from '../user-form/user-form.component';

@Component({
  selector: 'app-user-edit',
  imports: [
    CommonModule,
    RouterModule,
    UserFormComponent,
    PageHeaderComponent,
    DataStateComponent,
  ],
  templateUrl: './user-edit.component.html',
})
export class UserEditComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly usersService = inject(UsersService);
  private readonly destroyRef = inject(DestroyRef);

  readonly user = signal<User | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly submitting = signal(false);
  readonly serverError = signal<string | null>(null);

  private id!: number;

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const parsed = idParam ? Number(idParam) : NaN;
    if (!Number.isFinite(parsed)) {
      this.loadError.set('Identifiant invalide.');
      this.loading.set(false);
      return;
    }
    this.id = parsed;
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.usersService
      .get(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (u) => {
          this.user.set(u);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(
            toErrorMessage(err, 'Impossible de charger cet utilisateur.'),
          );
        },
      });
  }

  onSave(payload: UserFormPayload): void {
    this.submitting.set(true);
    this.serverError.set(null);

    this.usersService.patch(this.id, payload as PatchUserRequest).subscribe({
      next: () => {
        this.submitting.set(false);
        void this.router.navigate(['/users', this.id]);
      },
      error: (err: unknown) => {
        this.submitting.set(false);
        this.serverError.set(
          toErrorMessage(err, 'errors.updateFailed', {
            409: 'Identifiant, email ou téléphone déjà utilisé.',
          }),
        );
      },
    });
  }

  onCancel(): void {
    void this.router.navigate(['/users', this.id]);
  }
}
